import React, { useCallback, useEffect, useState } from 'react'
import { H2, Span, Flex, FlexCenter } from '../../lib/components'
import './faq.scss'
import { useNavigate } from 'react-router-dom'
import { faqServices } from '../../services/faq-service'
import { CircularProgress } from '@mui/material'
import { mapFaqResponseToSections } from './faq-utils'

const FAQ = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [cards, setCards] = useState<{ title: string; icon: React.ReactNode }[]>([])

  const load = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const data = await faqServices.getFaqs()
      const sections = mapFaqResponseToSections(data)
      setCards(
        sections.map((s) => ({
          title: s.title,
          icon: s.icon,
        })),
      )
    } catch {
      setLoadError('We could not load FAQs. Please try again later.')
      setCards([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleClick = (title: string) => {
    navigate(`/faq-details/${encodeURIComponent(title)}`)
  }

  return (
    <>
      <FlexCenter classes={['mb-40 mt-19']}>
        <H2 text='FAQ' classes={['mb-24 faq-page-title']} />
      </FlexCenter>
      {isLoading && (
        <FlexCenter classes={['faq-page-loading']}>
          <CircularProgress size={40} color='primary' />
        </FlexCenter>
      )}
      {!isLoading && loadError && (
        <FlexCenter classes={['faq-page-error']}>
          <Span text={loadError} classes={['faq-page-error-text']} />
        </FlexCenter>
      )}
      {!isLoading && !loadError && cards.length === 0 && (
        <FlexCenter classes={['faq-page-empty']}>
          <Span
            text='No FAQ content is available yet. Please check back soon.'
            classes={['faq-page-empty-text']}
          />
        </FlexCenter>
      )}
      {!isLoading && !loadError && cards.length > 0 && (
        <Flex
          variant='row'
          classes={['mb-32', 'faq-cards-container']}
          dataTestId='faq-cards-container'
        >
          {cards.map(({ title, icon }) => (
            <div
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick(title)
                }
              }}
              onClick={() => handleClick(title)}
              key={title}
              className='faq-card'
              data-testid={`faq-card-${title}`}
            >
              <Flex variant='column' classes={['faq-card-wrapper']}>
                <div className='faq-card-icon'>{icon}</div>
                <Span variant='bold' classes={['mb-8 faq-card-title']} text={title} />
              </Flex>
            </div>
          ))}
        </Flex>
      )}
    </>
  )
}

export { FAQ }
